import { cn } from 'cn';
import { memo, useCallback, useMemo } from 'react';
import { Icon, ParagraphWrapper } from '~/atoms';

import { downloadFile } from '@vestigas/utils';

import { type DataImportTemplate } from '../dataImport.config';
import {
  type DataImportCopyOverrides,
  useDataImportCopy,
} from '../dataImport.copy';

type InstructionsProps = {
  readonly additionalInformationLink?: string;
  /** The wizard's already-resolved copy; standalone use falls back to the defaults. */
  readonly copy?: DataImportCopyOverrides;
  /**
   * Every download to offer, already resolved. Reconciling the authoring
   * shapes — a legacy single `templateFile` against a definition's list — is
   * the config resolver's job, so this component never sees the choice.
   */
  readonly templates?: readonly DataImportTemplate[];
  readonly text: string | string[];
} & ComponentStyling;

const linkClassName =
  'block cursor-pointer space-x-2 px-4 py-2 text-sm text-tiga-action-default hover:text-tiga-action-lighter';

/**
 * Creates the object URL inside the click handler and frees it right after,
 * rather than holding one for as long as the template is offered. An object
 * URL an anchor's `href` points at has no owner that can free it without
 * leaving the href dangling — a remount frees the URL the rendered href still
 * names, and the browser then fails that download as a network error.
 */
const TemplateDownloadButton = memo(
  ({
    downloadLabel,
    template,
  }: {
    readonly downloadLabel: string;
    readonly template: DataImportTemplate;
  }) => {
    const { file, label } = template;

    const download = useCallback(() => {
      void downloadFile(file, file.name);
    }, [file]);

    return (
      <button
        className={cn(linkClassName, 'w-full text-left')}
        type="button"
        onClick={download}
      >
        <Icon metaphor="download" />
        <span>{label ?? downloadLabel}</span>
      </button>
    );
  },
);

const Instructions = memo(
  ({
    additionalInformationLink = '',
    className,
    copy: copyOverrides,
    style,
    templates,
    text,
  }: InstructionsProps) => {
    const copy = useDataImportCopy(copyOverrides);

    const templatesToRender = useMemo(
      () =>
        // A configured `id` survives the template file being re-fetched; the
        // position is only a tiebreaker for lists that declare none, where two
        // templates may still share a filename.
        (templates ?? []).map((template, index) => ({
          key: template.id ?? `${template.file.name}-${index}`,
          template,
        })),
      [templates],
    );

    return (
      <div className={cn('flex gap-4', className)} style={style}>
        <div className="flex-1">
          <ParagraphWrapper
            className="text-sm text-tiga-text-lighter"
            paragraphs={text}
            wrapIn="div"
          />
        </div>
        {templatesToRender.length > 0 || additionalInformationLink ? (
          <div className="justify-self-center rounded-md bg-[var(--mui-palette-Alert-infoStandardBg)] [&>:first-child]:pt-4 [&>:last-child]:pb-4">
            {additionalInformationLink ? (
              <a
                className={linkClassName}
                href={additionalInformationLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon metaphor="info" />
                <span>{copy.additionalInformation}</span>
              </a>
            ) : null}
            {templatesToRender.map(({ key, template }) => (
              <TemplateDownloadButton
                downloadLabel={copy.downloadTemplate}
                key={key}
                template={template}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);

export { Instructions };
